<template>
  <div class="vendor-charges-table table-component p-2">
    <artisio-table ref="vendorChargesTable" @paramsChanged="getTableData"
                   :sort="tableData.ordering" :items="tableData.rows" :busy="tableData.loading"
                   requires-min-height filter-toggle-string="vendor-charges-table"
                   :search="tableData.search" :enable-simple-search="false" :fields="tableFields"
                   :extra-data="extraData" selectable @selectedItemsChange="onSelectedItemsChange" show-toggle-columns
                   @update-toggle-columns="updateToggleColumns" @on-column-save="updateToggleColumns">
      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown :text="$t('Actions')" variant="primary">
          <b-dropdown-item @click="addCharge">
            <font-awesome-icon :icon="'plus'"/>
            {{ $t('Add Charge') }}
          </b-dropdown-item>
          <b-dropdown-item @click="deactivateSelected(selectedItems)">
            <font-awesome-icon :icon="'minus'"/>
            {{ $t('Deactivate Selected') }}
          </b-dropdown-item>
          <b-dropdown-item @click="deleteSelected(selectedItems)">
            <font-awesome-icon :icon="'trash'"/>
            {{ $t('Delete Selected') }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template v-slot:search-form-placeholder>
        <search-collapse table-id="vendor-charges-table">
          <vendor-charge-search/>
        </search-collapse>
      </template>
      <template slot="cell(name)" slot-scope="{item: charge}">
        {{ charge.name }}
        <action-buttons v-if="charge.level == source && !charge.data.func_name"
                        :view="false"
                        @deleteClick="onDeleteChargeClick(charge)"
                        @editClick="updateCharge(charge)"/>
        <action-buttons v-if="charge.level == source && charge.data.func_name && source != 'branch'"
                        :view="false"
                        :edit="false"
                        @deleteClick="onDeleteChargeClick(charge)"/>
        <action-buttons v-if="charge.level == source && charge.data.func_name && source == 'branch'"
                        :view="false"
                        :remove="false"
                        @editClick="updateCharge(charge)"/>
        <action-buttons v-if="
          charge.level != source &&
          (source == 'lot' && charge.data.applies_to == 'lot' ||
          source == 'inventory' && (charge.data.applies_to == 'inventory' || charge.data.applies_to == 'lot') ||
          source != 'lot' && source != 'inventory')"
                        :view="false"
                        :remove="false"
                        :edit="false"
                        :extra-actions="computedExtraActions"
                        @editClick="updateCharge(charge, true)"/>
      </template>
      <template slot="cell(level)" slot-scope="{item: charge}">
        {{ $t($options.filters.toUpperCaseFirst(charge.level)) }}
      </template>
      <template slot="cell(applies_to)" slot-scope="{item: charge}">
        {{ $t($options.filters.toUpperCaseFirst(charge.data.applies_to)) }}
      </template>
      <template slot="cell(lot_statuses)" slot-scope="{item: charge}">
        <span class="badge border mr-1" v-for="status in (charge.data.lot_statuses ? charge.data.lot_statuses.split(',') : [])"
              :key="`customer-table-status-${charge.data.uuid}-${status}`">{{ status | removeUnderscores | toUpperCaseEveryWord }}</span>
      </template>
      <template slot="cell(calculate_from)" slot-scope="{item: charge}" v-if="!charge.data.func_name">
        <span class="badge border mr-1" v-for="source in charge.data.calculate_from.split(',')"
              :key="`customer-table-source-${charge.data.uuid}-${source}`">
          <template v-if="source == 'vendor_financial_price'">Hammer Price<br/>(Excluding VAT – France Only)</template>
          <template v-else>{{ source | removeUnderscores | toUpperCaseEveryWord }}</template>
        </span>
      </template>
      <template slot="cell(is_aggregate)" slot-scope="{item: charge}" v-if="!charge.data.func_name">
        <span v-html="$options.filters.asBadgeBoolean(charge.data.is_aggregate)"/>
      </template>
      <template slot="cell(is_taxable)" slot-scope="{item: charge}">
        <span v-html="$options.filters.asBadgeBoolean(charge.data.is_taxable)"/>
      </template>
      <template slot="cell(is_tax_inclusive)" slot-scope="{item: charge}">
        <span v-html="$options.filters.asBadgeBoolean(charge.data.is_tax_inclusive)"/>
      </template>
      <template slot="cell(is_active)" slot-scope="{item: charge}">
        <span v-html="$options.filters.asBadgeBoolean(charge.data.is_active)"/>
      </template>
      <template slot="cell(tiers)" slot-scope="{item: charge}">
        <template v-for="tier in charge.data.tiers">
          <template v-if="tier.percent">{{ tier.percent | asPercentage }} - {{ tier.amount_over | asCurrency }}</template>
          <template v-if="tier.flat_price">{{ tier.flat_price | asCurrency }}</template>
          <br/>
        </template>
      </template>
      <template slot="cell(tax_rate)" slot-scope="{item: charge}">
        <template v-if="charge.data.is_taxable">{{ charge.data.tax_rate | asPercentage }}</template>
      </template>
      <template slot="cell(created_data)" slot-scope="{item: charge}" v-if="charge.data.created_by">
        {{ charge.data.created_at | toDate }} by {{ charge.data.created_by.display_name }}
      </template>
    </artisio-table>
    <vendor-charge-modal/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "@/core/components/ams-table/ArtisioTable";
  import VendorChargeSearch from "./VendorChargeSearch";
  import SearchCollapse from "@/core/components/search-collapse/SearchCollapse";
  import i18n from "@/shared/i18n";
  import {VENDOR_CHARGES_TABLE_NAME} from "../constants";
  import VendorChargeModal from './VendorChargeModal'
  import ActionButtons from "@/core/components/ams-table/ActionButtons";
  import VendorChargesService from "./VendorChargesService";
  import cloneDeep from "lodash/cloneDeep";

  const {mapState, mapActions} = createNamespacedHelpers('vendorCharges');
  const {mapState: mapStateBranch} = createNamespacedHelpers('setup/branch');

  export default {
    name: "VendorChargesTable",
    components: {
      SearchCollapse, VendorChargeSearch, ArtisioTable, VendorChargeModal, ActionButtons
    },
    data: () => {
      return {
        selectedItems: new Set()
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        source: state => state.tableSource,
        sourceUuid: state => state.tableSourceUuid,
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
      }),
      ...mapStateBranch({
        branch: state => state.selectedBranch
      }),
      tableFields() {
        return cloneDeep(this.toggleColumns.fields).filter(field => field.isVisible);
      },
      extraData() {
        return {
          ...this.toggleColumns,
          tableName: `${VENDOR_CHARGES_TABLE_NAME}.${this.source}`,
        }
      },
      computedExtraActions() {
        return [{
          index: 0,
          data: {
            text: this.$i18n.t('Override'),
            emitKey: 'editClick',
            textClass: 'text-primary',
          }
        }];
      }
    },
    methods: {
      ...mapActions(['showVendorChargeModal', 'deleteCharge', 'getTableData', 'updateToggleColumns']),
      onSelectedItemsChange(selectedItems) {
        this.selectedItems = selectedItems;
      },
      addCharge() {
        this.showVendorChargeModal();
      },
      async deactivateSelected(items) {
        if (items.size == 0) {
          return;
        }
        let response = await VendorChargesService.batchDeactivate({
          'ids': Array.from(items, item => item.uuid),
          'level': this.source,
          'obj_uuid': this.sourceUuid
        });
        if (response.success) {
          this.$toast('success', this.$i18n.t('Charges have been successfully deactivated'));
          this.getTableData();
        } else {
          if (response.body.failed) {
            this.$toast('danger', this.$i18n.t('Failed to deactivate'));
          } else {
            let msg = this.$i18n.t('The following charges are already used');
            this.$toast('danger', msg + ': ' + response.body.used.join(', '));
          }
        }
      },
      async deleteSelected(items) {
        if (items.size == 0) {
          return;
        }
        if (this.source == 'branch') {
          let functionCharges = Array.from(items, item => item.data.func_name ? item.name : null).filter(Boolean);
          if (functionCharges.length > 0) {
            let msg = this.$i18n.t("Charges with predefined functions can't be deleted: ") + functionCharges.join(', ');
            this.$toast('danger', msg);
            return;
          }
        }
        let response = await VendorChargesService.batchDelete({
          'ids': Array.from(items, item => item.uuid),
          'level': this.source,
          'obj_uuid': this.sourceUuid
        });
        if (response.success) {
          this.$toast('success', this.$i18n.t('Charges have been successfully deleted'));
          this.getTableData();
        } else {
          let msg = this.$i18n.t('Unable to delete some charges');
          if (response.body.used) {
            msg = msg + '. ' + this.$i18n.t('The following charges are already used') + ': ' + response.body.used.join(', ');
          }
          if (response.body.higher) {
            msg = msg + '. ' + this.$i18n.t('The following charges are from higher level') + ': ' + response.body.higher.join(', ');
          }
          this.$toast('danger', msg);
        }
      },
      async updateCharge(charge, isOverride) {
        let params = {'uuid': charge.uuid};
        if (isOverride) {
          params['level'] = this.source;
          params['obj_uuid'] = this.sourceUuid;
        }
        let response = await VendorChargesService.checkUsed(params);
        if (response.success && response.body.used.length == 0) {
          this.showVendorChargeModal(charge);
        } else {
          let msg = this.$i18n.t('Unable to edit charge, because it is already used in settlement(s)');
          if (response.body.used.length > 0) {
            msg = msg + ': ' + response.body.used.join(', ');
          }
          this.$toast('danger', msg);
        }
      },
      async onDeleteChargeClick(charge) {
        let success = await this.$confirm(
            this.$i18n.t('Are you sure you want to delete the charge?'),
            this.$i18n.t('Charge has been successfully deleted'),
            this.deleteCharge,
            charge
        );
        if (!success) {
          return;
        }
        this.getTableData();
      }
    }
  }
</script>

<style lang="scss" scoped>
  .artisio-table {
    /deep/ {
      .calculate-from-col {
        max-width: 120px;
        white-space: pre-wrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .calculate-from-col > span {
        text-align: left;
      }
      .lot-statuses-col {
        max-width: 100px;
        white-space: pre-wrap;
      }
      .is-aggregate-col, .is-taxable-col, .is-tax-inclusive-col, .is-active-col {
        text-align: center;
      }
    }
  }
</style>

